import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  InlineFieldGroup,
  InlineFieldInput,
  InlineFieldTextarea,
  type InlineGroupField,
} from "@/components/inline-field-group";
import { LoadingButton } from "@/components/loading-button";
import {
  type ManualProduct,
  useCreateManualProduct,
  useUpdateManualProduct,
} from "@/api/manual-products";
import { getApiErrorMessage } from "@/lib/api-error";

type ProductField = "name" | "model" | "description";

const MAX_LENGTH: Record<ProductField, number> = {
  name: 200,
  model: 100,
  description: 2000,
};

const FIELDS: ReadonlyArray<InlineGroupField<ProductField>> = [
  { key: "name", label: "Name", required: true },
  { key: "model", label: "Model", required: true },
  { key: "description", label: "Description", kind: "textarea" },
];

/** Fields longer than the server accepts; the boxes for a new product cap them as you type. */
function tooLong(draft: Record<ProductField, string>): ProductField[] {
  return FIELDS.map(({ key }) => key).filter(
    (key) => draft[key].length > MAX_LENGTH[key],
  );
}

/**
 * Add or edit a product we define for a manufacturer AutoQuotes doesn't carry, such as a
 * Safeware warranty plan (USA-1108). Retiring one stops quoting from offering it.
 */
export function ManualProductDialog({
  manufacturerUuid,
  product,
  open,
  onOpenChange,
}: {
  manufacturerUuid: string;
  /** The product to edit; null adds a new one. */
  product: ManualProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {open ? (
          <ManualProductForm
            key={product?.uuid ?? "new"}
            manufacturerUuid={manufacturerUuid}
            product={product}
            onDone={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ManualProductForm({
  manufacturerUuid,
  product,
  onDone,
}: {
  manufacturerUuid: string;
  product: ManualProduct | null;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  // A saved product opens straight into editing; Done or Cancel shows its details (USA-1580).
  const [editing, setEditing] = useState(true);
  const create = useCreateManualProduct(manufacturerUuid);
  const update = useUpdateManualProduct(manufacturerUuid);
  const busy = create.isPending || update.isPending;
  const retired = Boolean(product?.discontinued_at);
  const canSave = Boolean(name.trim() && model.trim());

  const send = (draft: Record<ProductField, string>) => {
    if (!draft.name.trim() || !draft.model.trim() || busy) return;
    const body = {
      name: draft.name.trim(),
      model: draft.model.trim(),
      description: draft.description.trim() || null,
    };
    const onError = (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Could not save the product"));
    if (product) {
      update.mutate(
        { productUuid: product.uuid, body },
        {
          onSuccess: () => {
            toast.success(`${body.model} saved`);
            onDone();
          },
          onError,
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          toast.success(`${body.model} added`);
          onDone();
        },
        onError,
      });
    }
  };

  const save = () => {
    if (!canSave) return;
    send({ name, model, description });
  };

  const toggleRetired = () => {
    if (!product || busy) return;
    update.mutate(
      { productUuid: product.uuid, body: { retired: !retired } },
      {
        onSuccess: () => {
          toast.success(
            retired ? `${product.model} restored` : `${product.model} retired`,
          );
          onDone();
        },
        onError: (error) =>
          toast.error(
            getApiErrorMessage(error, "Could not change the product"),
          ),
      },
    );
  };

  if (product) {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle className="text-base">{`Edit ${product.model}`}</DialogTitle>
        </DialogHeader>
        <InlineFieldGroup
          label="Product"
          fields={FIELDS}
          values={{
            name: product.store_title ?? "",
            model: product.model ?? "",
            description: product.specifications ?? "",
          }}
          invalid={tooLong}
          pending={
            update.isPending && update.variables?.body.retired === undefined
          }
          disabled={busy}
          editing={editing}
          onEditingChange={setEditing}
          onSave={send}
        >
          <span className="block text-sm font-medium">
            {product.store_title || "No name"}
          </span>
          <span className="block font-mono text-xs text-muted-foreground">
            {product.model}
          </span>
          <span className="mt-1 block whitespace-pre-wrap text-sm text-muted-foreground">
            {product.specifications || "No description"}
          </span>
        </InlineFieldGroup>
        <DialogFooter className="gap-2 sm:justify-start">
          <LoadingButton
            type="button"
            variant="outline"
            loading={
              update.isPending && update.variables?.body.retired !== undefined
            }
            disabled={busy}
            onClick={toggleRetired}
          >
            {retired ? "Restore" : "Retire"}
          </LoadingButton>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <DialogHeader>
        <DialogTitle className="text-base">Add product</DialogTitle>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="manual-product-name">Name</Label>
        <InlineFieldInput
          id="manual-product-name"
          value={name}
          maxLength={MAX_LENGTH.name}
          onChange={(event) => setName(event.target.value)}
          autoFocus
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="manual-product-model">Model</Label>
        <InlineFieldInput
          id="manual-product-model"
          value={model}
          maxLength={MAX_LENGTH.model}
          onChange={(event) => setModel(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="manual-product-description">Description</Label>
        <InlineFieldTextarea
          id="manual-product-description"
          value={description}
          maxLength={MAX_LENGTH.description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <DialogFooter className="gap-2">
        <LoadingButton
          type="submit"
          loading={create.isPending}
          loadingText="Saving..."
          disabled={!canSave || busy}
        >
          <Plus className="size-4" />
          Add product
        </LoadingButton>
      </DialogFooter>
    </form>
  );
}
